import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { SHAM_DASH_CONTENT_IMPORTS } from 'sham-components/features/dashboard';
import { DxButtonModule, DxDateBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SHARED_PROFILE_ADAPTER, SHARED_PROFILE_CONFIG } from './profile.tokens';
import { ResetPasswordButton } from './reset-password-button';
import {
  SharedProfileFieldConfig,
  SharedProfileValues,
} from './profile.types';

@Component({
  selector: 'sham-shared-profile-page',
  imports: [
    SHAM_DASH_CONTENT_IMPORTS,
    DxTextBoxModule,
    DxDateBoxModule,
    DxButtonModule,
    ResetPasswordButton,
  ],
  templateUrl: './profile.html',
  styleUrl: './profile.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedProfilePage {
  private readonly adapter = inject(SHARED_PROFILE_ADAPTER);
  private readonly destroyRef = inject(DestroyRef);
  readonly config = inject(SHARED_PROFILE_CONFIG);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal('');
  readonly saveSuccess = signal('');
  readonly values = signal<SharedProfileValues>({});
  readonly profileEditable = signal(true);
  readonly rejectionReason = signal('');

  readonly visibleFields = computed(() =>
    this.config.fields.filter((field) => this.shouldShowField(field)),
  );

  constructor() {
    this.loading.set(true);
    this.adapter
      .loadProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value) => {
          this.values.set(value.values);
          this.profileEditable.set(value.profileEditable);
          this.rejectionReason.set(value.rejectionReason ?? '');
          this.loading.set(false);
        },
        error: () => {
          this.saveError.set('Failed to load profile.');
          this.loading.set(false);
        },
      });
  }

  onFieldChange(key: string, value: unknown) {
    if (value instanceof Date) {
      const dateOnly = value.toISOString().split('T')[0] ?? '';
      this.values.update((current) => ({ ...current, [key]: dateOnly }));
      return;
    }
    this.values.update((current) => ({ ...current, [key]: String(value ?? '') }));
  }

  async onSave() {
    this.saving.set(true);
    this.saveError.set('');
    this.saveSuccess.set('');
    this.adapter
      .saveProfile(this.values())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saveSuccess.set('Profile updated successfully.');
          this.saving.set(false);
        },
        error: () => {
          this.saveError.set('Failed to update profile.');
          this.saving.set(false);
        },
      });
  }

  inputMode(field: SharedProfileFieldConfig): 'text' | 'email' | 'tel' {
    if (field.inputType === 'email') return 'email';
    if (field.inputType === 'phone') return 'tel';
    return 'text';
  }

  private shouldShowField(field: SharedProfileFieldConfig): boolean {
    if (!field.showFor || !field.showFor.length) return true;
    return field.showFor.includes(this.config.userType);
  }
}
